import { useTranslation } from 'react-i18next';
import { FC } from 'react';
import { homeTranslations, kpiTargetTranslations } from '../../../../../../translations';
import { WidgetMetricTile, WidgetMetricType } from '../../../../common/metricTile/WidgetMetricTile';
import { WidgetMetricTilesContainer } from '../../../../common/metricTile/WidgetMetricTile.styles';
import { getDefaultStatus } from '../../../../common/metricTile/util';
import { StyledTrendIconComponent } from '../../../../common/styledTrendIcon/StyledTrendIcon';
import { SalesNetComparisonValues } from '../util';
import { CurrencyFormatterOptions } from '../../../../../../hooks/useCurrencyTools';

type SalesNetDesktopContentProps = {
  showForecast: boolean;
  totalNetSales: number;
  forecastTotalNetSales: number;
  targetProgressPercent: number;
  isKpiLoading: boolean;
  isKpiError: boolean;
  isToday: boolean;
  target: number | null;
  showTarget: boolean;
  activeComparison: SalesNetComparisonValues;
  getFormattedCurrency: (value: number, options?: CurrencyFormatterOptions) => string;
};

export const SalesNetDesktopContent: FC<SalesNetDesktopContentProps> = ({
  showForecast,
  totalNetSales,
  forecastTotalNetSales,
  targetProgressPercent,
  getFormattedCurrency,
  isKpiLoading,
  isKpiError,
  isToday,
  target,
  showTarget,
  activeComparison,
}) => {
  const { t } = useTranslation();
  const { common: commonTranslations, hourlyTurnover } = homeTranslations;
  const changeNote = isToday
    ? ''
    : t(commonTranslations.changeVsCurrentNote.key, commonTranslations.changeVsCurrentNote.defaultValue);

  return (
    <WidgetMetricTilesContainer>
      {showForecast && (
        <WidgetMetricTile
          metricProperties={{
            type: WidgetMetricType.ForecastMetric,
            properties: {
              metricValue: getFormattedCurrency(forecastTotalNetSales),
              forecastIndicator: getDefaultStatus(targetProgressPercent),
              metricTooltipText: t(
                commonTranslations.forecastTileTooltip.key,
                commonTranslations.forecastTileTooltip.defaultValue,
              ),
            },
          }}
        />
      )}

      <WidgetMetricTile
        metricProperties={{
          type: WidgetMetricType.LegendMetric,
          properties: {
            metricTitle: t(hourlyTurnover.totalSales.key, hourlyTurnover.totalSales.defaultValue),
            metricValue: getFormattedCurrency(totalNetSales),
            legendColor: 'validationGreen',
            metricTooltipText: t(
              hourlyTurnover.metricTileTooltip.totalSalesTile.key,
              hourlyTurnover.metricTileTooltip.totalSalesTile.defaultValue,
            ),
          },
        }}
      />

      {showTarget && target !== null && (
        <WidgetMetricTile
          isLoading={isKpiLoading}
          isError={isKpiError}
          metricProperties={{
            type: WidgetMetricType.LegendMetric,
            properties: {
              metricTitle: t(
                kpiTargetTranslations.kpiProperties.target.title.key,
                kpiTargetTranslations.kpiProperties.target.title.defaultValue,
              ),
              metricValue: getFormattedCurrency(target),
              legendColor: 'tagBlue',
              metricTooltipText: t(
                hourlyTurnover.metricTileTooltip.targetTile.key,
                hourlyTurnover.metricTileTooltip.targetTile.defaultValue,
              ),
            },
          }}
        />
      )}

      <WidgetMetricTile
        metricProperties={{
          type: WidgetMetricType.RegularMetric,
          properties: {
            metricTitle: t(commonTranslations.lastWeek.key, commonTranslations.lastWeek.defaultValue),
            metricTooltipText: t(
              hourlyTurnover.metricTileTooltip.lastWeekTile.key,
              hourlyTurnover.metricTileTooltip.lastWeekTile.defaultValue,
              { changeNote },
            ),
            metricValue: (
              <StyledTrendIconComponent
                originalAmount={activeComparison.today}
                previousAmount={activeComparison.lastWeek}
                direction="horizontal"
                valueFormatter={(value) => getFormattedCurrency(value)}
                textOptions={{ type: 'caption' }}
                hasPercentage={false}
                hideTrendText={isToday}
              />
            ),
          },
        }}
      />

      <WidgetMetricTile
        metricProperties={{
          type: WidgetMetricType.RegularMetric,
          properties: {
            metricTitle: t(commonTranslations.lastYear.key, commonTranslations.lastYear.defaultValue),
            metricTooltipText: t(
              hourlyTurnover.metricTileTooltip.lastYearTile.key,
              hourlyTurnover.metricTileTooltip.lastYearTile.defaultValue,
              { changeNote },
            ),
            metricValue: (
              <StyledTrendIconComponent
                originalAmount={activeComparison.today}
                previousAmount={activeComparison.lastYear}
                direction="horizontal"
                valueFormatter={(value) => getFormattedCurrency(value)}
                textOptions={{ type: 'caption' }}
                hasPercentage={false}
                hideTrendText={isToday}
              />
            ),
          },
        }}
      />
    </WidgetMetricTilesContainer>
  );
};
